import { TargetFactorRanges } from '../constants-types/constants';
import { JobTargetCreationMethod } from '../constants-types/Enums';
import { Util } from '../core-utilities/Util';
import { BehavioralFactorRanges } from './BehavioralFactorRanges.model';
import { BenchmarkOccupation } from './BenchmarkOccupation.model';
import { BenchmarkProfile } from './BenchmarkProfile.model';
import { CognitiveJobAssessment } from './CognitiveJobAssessment.model';
import { MinMax } from './Common.model';
import { Folder } from './Folder.model';
import { JobPipelineHealth } from './Job/JobPipelineHealth.model';
import { RefProfileReportInfo } from './Job/JobReportInfo.model';
import { JobStub } from './Job/JobStub.model';
import { JobAssessment } from './JobAssessment.model';
import { ResultBasics } from './ResultBasics.model';
import { SearchResult } from './SearchResult.model';

const defaultRange = new MinMax({
    min: -0.25,
    max: 0.25
});
export const defaultBehavioralRangesSet = {
    A: defaultRange,
    B: defaultRange,
    C: defaultRange,
    D: defaultRange
};
export const defaultCogScore = 250; // Middle
export class JobIdentifierPlus extends JobStub {
    readonly createdByName: string;
    constructor(startingValues?: Partial<JobMatchingPerson>) {
        super();
        Object.assign(this, startingValues);
    }
}

export class JobMatchingPerson extends JobIdentifierPlus {
    readonly personFit: PersonFit;
    readonly behavioralRanges?: BehavioralFactorRanges;
    readonly createdDate?: Date;
    readonly cognitiveTarget?: number;

    constructor(startingValues?: Partial<JobMatchingPerson>) {
        super();
        Object.assign(this, startingValues);
    }
}

export class JobFromIndex extends JobStub {
    readonly behavioralRanges?: BehavioralFactorRanges;
    readonly folder?: Folder;
    readonly jobPipeline: JobPipelineHealth;
    readonly createdDate?: Date;
    readonly lastUpdated: Date;

    constructor(startingValues?: Partial<Job>) {
        super();
        Object.assign(this, startingValues);
    }

    get hasBehavioralJobTarget(): boolean {
        return Util.hasValue(this.behavioralRanges);
    }
}

export class Job extends JobFromIndex implements ResultBasics {
    readonly description: string;
    readonly companyId: string;
    readonly onetSocCode: string | null;
    readonly createdByName: string;
    readonly createdByFirstName?: string;
    readonly createdByLastName?: string;
    readonly createdByEmail?: string;
    readonly createdById?: string;
    readonly modifiedDate?: Date;
    readonly lastModifiedBy?: string;
    readonly cognitiveBaselineScaledScore: number = defaultCogScore;
    readonly cognitiveAssessments: CognitiveJobAssessment[] = [];
    // This allows storing when a target is set, the method
    readonly jobTargetCreationMethod: JobTargetCreationMethod;
    readonly benchmarkOccupation: BenchmarkOccupation;
    readonly cognitiveTarget?: number;
    readonly cognitiveWeight: number = 0.5;
    readonly benchmarkProfiles: BenchmarkProfile[];
    // matching profiles
    readonly matchingProfiles: RefProfileReportInfo[];
    readonly hireVersion: string;
    readonly isOwner?: boolean;
    readonly isPending?: boolean;
    readonly ownedById?: string;
    readonly ownedByFirstName?: string;
    readonly ownedByLastName?: string;
    readonly ownedByEmail?: string;
    constructor(startingValues?: Partial<Job>) {
        super();
        // TODO this should probably be replaced by a factory
        Object.assign(this, startingValues);
    }

    static normalizeNullJobTarget(job: Job): {
        behavioralRanges: BehavioralFactorRanges;
        cogScore: number;
    } {
        return {
            behavioralRanges:
                job.behavioralRanges || defaultBehavioralRangesSet,
            cogScore: job.cognitiveTarget || defaultCogScore
        };
    }

    get useCognitiveForMatch(): boolean {
        return this.cognitiveWeight > 0;
    }

    get hasTarget(): boolean {
        return this.hasBehavioralJobTarget || this.hasCognitiveJobTarget;
    }

    get hasCognitiveJobTarget(): boolean {
        return Util.hasValue(this.cognitiveTarget) && this.cognitiveTarget > 0;
    }

    get hasCandidates(): boolean {
        return this.jobPipeline && this.jobPipeline.totalCandidates > 0;
    }

    hasBenchmark() {
        return (
            this.onetSocCode &&
            !this.onetSocCode.includes('No Match') &&
            this.benchmarkProfiles &&
            this.benchmarkProfiles.length > 0
        );
    }

    getJobPageUrl(): string {
        return `/job/${this.id}/overview`;
    }

    isJobCreator(): boolean {
        return this.isOwner;
    }
}

export class JobWithAssessments extends Job {
    readonly assessments: JobAssessment[] = [];

    constructor(startingValues?: Partial<JobWithAssessments>) {
        super();
        // TODO this should probably be replaced by a factory
        Object.assign(this, startingValues);
    }
}

export class JobWithEverything extends JobWithAssessments {
    readonly roleModels: SearchResult[] = [];

    constructor(startingValues?: Partial<JobWithEverything>) {
        super();
        // TODO this should probably be replaced by a factory
        Object.assign(this, startingValues);
    }
}

export class JobUpdate {
    originalObject: Job | null;

    title: string;
    description: string;
    folder: Folder;
    benchmarkOccupation: BenchmarkOccupation;
    behavioralRanges: BehavioralFactorRanges;
    cognitiveTarget: number;
    cognitiveWeight: number;
    jobTargetCreationMethod: JobTargetCreationMethod;
    hireVersion: string;
    id?: string;
    targetFactorRanges?: TargetFactorRanges;
    jobAssesmentLiteCreate: JobAssessmentLiteCreate;
    constructor(
        firstArgument: Job | Partial<JobUpdate>,
        secondArgument?: Partial<JobUpdate>
    ) {
        if (firstArgument instanceof Job) {
            this.originalObject = firstArgument;
        }
        Object.assign(this, firstArgument, secondArgument);
    }

    optimisticUpdate() {
        return new Job(Object.assign({}, this.originalObject, this));
    }
}

export class PersonFit {
    overallFitScore: number;
    behavioralFitScore: number;
    cognitiveFitScore: number;

    constructor(obj?: any) {
        Object.assign(this, obj);
    }
}

// Should be moved to client-hire once we move over the JobAPI/datastore service
export type JobAssessmentLiteCreate = {
    definitionId: string;
    selectedQuestionIds: string[];
    type: number;
};
